import { RSAA } from 'redux-api-middleware';
import { nanoid } from 'nanoid';

import { CUDL_PROVIDER_CONFIG, PushToFIStatus, type PushToFIProvider, type StateTree } from '@makemydeal/dr-dash-types';
import { apiConfigHelper, locHrefUtil, MANAGER_VIEW_APP_PREFIX } from '@makemydeal/dr-shared-ui-utils';
import {
    CLEAR_PUSH_TO_FI_CACHE,
    CONFIRM_PUSH_TO_FI,
    PUSH_TO_FI_DEAL_REF_ID_TIMEOUT_EVENT,
    PUSH_TO_FI_FAILURE,
    PUSH_TO_FI_OFFER_SAVE_FAILURE_EVENT,
    PUSH_TO_FI_REQUEST,
    PUSH_TO_FI_SUCCESS,
    UPDATE_PUSH_TO_FI
} from '../actionTypes/pushToFIActionTypes';
import { getDealXgId, getDealXgVersion, getDealRefIdFS, getCurrentOfferType } from '../selectors/offerRedux';
import { getPushToFIStatus } from '../selectors/pushToFISelectors';
import { getSelectedCreditDecision } from '../selectors/creditDecisionSelectors';
import { dealerSelectors } from '@makemydeal/dr-shared-store';
import { getContractingSourcePartnerId } from './contractingActionCreators';

export type PushToFIWebhookResult = {
    timestamp: string;
    message: string;
    ok: boolean;
};

export const confirmPushToFI = (providerId: string) => {
    return {
        type: CONFIRM_PUSH_TO_FI,
        payload: { providerId, correlationId: nanoid(), initiatedAt: Date.now() }
    };
};

export const updatePushToFI = (providerId: string, { timestamp, message, ok }: PushToFIWebhookResult) => {
    const status: string = ok ? PushToFIStatus.Success : PushToFIStatus.Failure;
    return {
        type: UPDATE_PUSH_TO_FI,
        payload: {
            providerId,
            timestamp,
            message,
            ok,
            status
        }
    };
};

export const pushToFIOfferSaveFailed = (providerId: string) => {
    return {
        type: PUSH_TO_FI_OFFER_SAVE_FAILURE_EVENT,
        payload: {
            providerId,
            message: 'Failed to Push to FI',
            ok: false,
            stage: 'DealAPI'
        }
    };
};

export const pushToFIDealRefIdTimedOut = (providerId: string) => {
    return {
        type: PUSH_TO_FI_DEAL_REF_ID_TIMEOUT_EVENT,
        payload: {
            providerId,
            message: 'Timed out waiting for a deal reference id',
            ok: false,
            stage: 'DealRefId'
        }
    };
};

export const clearPushToFICache = (providerId: string) => {
    return {
        type: CLEAR_PUSH_TO_FI_CACHE,
        payload: { providerId }
    };
};

const getPartyIdForProvider = (provider: PushToFIProvider, state: StateTree): string => {
    if (provider.targetPlatformId === CUDL_PROVIDER_CONFIG.targetPlatformId) {
        return dealerSelectors.getCudlPartyId(state);
    }
    return dealerSelectors.getRouteOnePartyId(state);
};

export type PushToFIPayloadOverrides = { dealXgId?: string; dealXgVersion?: string };

export const pushToFIRSAA = (
    appPrefix: string,
    state: StateTree,
    provider: PushToFIProvider,
    overrides: PushToFIPayloadOverrides = {}
) => {
    const offerType = (state: StateTree) => {
        const value = getCurrentOfferType(state);
        if (value === 'finance') return 'Finance';
        else return 'Lease';
    };

    const dealRefId = getDealRefIdFS(state);
    const dealXgId = overrides.dealXgId || getDealXgId(state);
    const dealXgVersion = overrides.dealXgVersion || getDealXgVersion(state);
    const sourceApplication = appPrefix === MANAGER_VIEW_APP_PREFIX ? 'ManagerView' : 'DealerView';
    const lenderId = getSelectedCreditDecision(state).lenderId;
    const financeMethod = offerType(state);
    const sourcePartnerId = getContractingSourcePartnerId(state);
    const correlationId = getPushToFIStatus(state, provider.targetPlatformId)?.correlationId || nanoid();
    const partyId = getPartyIdForProvider(provider, state);

    const dealerId = dealerSelectors.getDealerId(state);
    const locHref = locHrefUtil.getLocHref();
    const bffApiUrlResult = apiConfigHelper.buildBffApiUrl(MANAGER_VIEW_APP_PREFIX, locHref, '/pushToFI');
    const endpoint = bffApiUrlResult.url;

    const body = JSON.stringify({
        sourcePartnerId,
        sourceApplication,
        dealerId,
        dealRefId,
        dealXgDealId: dealXgId,
        dealXgDealVersion: dealXgVersion,
        financeMethod,
        targetPlatforms: [
            {
                id: provider.targetPlatformId,
                partyId
            }
        ],
        lenderId
    });

    const meta = { providerId: provider.targetPlatformId };

    return {
        [RSAA]: {
            endpoint,
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'x-coxauto-correlation-id': correlationId
            },
            body,
            types: [
                { type: PUSH_TO_FI_REQUEST, meta },
                { type: PUSH_TO_FI_SUCCESS, meta },
                { type: PUSH_TO_FI_FAILURE, meta }
            ]
        }
    };
};
